import type { TTemplateContent } from "models";

/**
 * A selectable entry in the template gallery.
 *
 * The full template document is loaded lazily (via dynamic `import()`) so the
 * gallery metadata stays light and the preset JSON is only pulled into the
 * bundle when a user actually picks it.
 */
export interface TemplateGalleryItem {
  /** Stable id for the gallery entry. */
  id: string;
  /** User-facing name shown in the picker. */
  name: string;
  /** Short blurb describing the style. */
  description: string;
  /**
   * Thumbnail image URL. Left undefined for now — the picker renders a blank
   * placeholder. JSON->thumbnail generation (from the web portal) can populate
   * this later.
   */
  thumbnailUrl?: string;
  /** Lazily resolves the full template document. */
  load: () => Promise<TTemplateContent>;
}

/**
 * The available starter templates. These are placeholders (title only) until
 * real designs are produced via claude.ai/design.
 *
 * NOTE: this is an in-app picker (the app runs inside an iframe in the Angular
 * portal and is effectively a single screen) — selection is UI state, not a
 * route.
 */
export const TEMPLATE_GALLERY: TemplateGalleryItem[] = [
  {
    id: "classic",
    name: "Classic",
    description: "The original default defect report layout.",
    load: () => import("./presets/classic").then((m) => m.default()),
  },
  {
    id: "centered-minimal",
    name: "Centered Minimal",
    description: "Logo + text only, centered, generous whitespace. No hero image. Formal & clean.",
    load: () => import("./presets/cover-pages").then((m) => m.centeredMinimal()),
  },
  {
    id: "two-column",
    name: "Two Column",
    description: "Full-width hero image leads, bold left title, two-column details with signature, split footer.",
    load: () => import("./presets/cover-pages").then((m) => m.twoColumn()),
  },
];

/** Look up a gallery entry by id. */
export const getGalleryItem = (id: string): TemplateGalleryItem | undefined =>
  TEMPLATE_GALLERY.find((item) => item.id === id);
